import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMCategoryProgressCard,
  PMDonutChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../../app";
import { APP_ROUTES } from "../../../constants";
import { getDropdownCount } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../../utils/get-plural-of-project-name";
import { getOverviewCardCopy } from "../get-card-copy";

export function addCompletionAndCategoryBreakdownCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const {
    itemName,
    itemNamePlural,
    projectName,
    projectNamePlural,
    getCardCopy,
  } = getOverviewCardCopy(app);

  const completionOfProjectsComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "completionOfProjectsComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "maturity"],
    },
  });

  completionOfProjectsComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completionOfProjectsComponent",
      labels: ["donutChart", "chart", "maturity"],
      title: getCardCopy(language, "completionOfProjects").title,
      description: getCardCopy(language, "completionOfProjects").description,
    };
  });

  completionOfProjectsComponent.mapState((appState) => {
    const projectDegreeOfCompletionCounts =
      appState.dashboard?._calculated?.projectDegreeOfCompletionCounts ?? {};
    const data =
      appState.settings?.configuration?.degreeOfCompletionDefinition?.map(
        (entry) => {
          return {
            label: entry.name ?? "",
            value: getDropdownCount(projectDegreeOfCompletionCounts, entry),
            color: entry.color ?? "",
          };
        }
      ) ?? [];

    const handleCompletionFilter = (completion?: string) => {
      app?.patchAppState({
        filterData: {
          searchQueryFilter: [],
          statusFilter: [],
          completionFilter: completion ? [completion] : [],
          categoryFilter: [],
          updateStatusFilter: [],
        },
      });
      app?.navigate(APP_ROUTES.overview);
    };

    return {
      title: getLocalizedCardCopy(
        appState.language || "en",
        "completionOfProjects",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).title,
      icon: "DonutLarge",
      data,
      onCardClick: () => handleCompletionFilter(),
      onSegmentClick: (segmentIndex: number) =>
        handleCompletionFilter(data[segmentIndex]?.label),
    };
  });

  const completedProjectsCategoryComponent = view.addComponent({
    component: PMCategoryProgressCard,
    layoutProps: {
      componentId: "completedProjectsCategoryComponent",
      title: "",
      description: "",
      labels: ["progress"],
    },
  });

  completedProjectsCategoryComponent.mapLayoutProps(
    ({ language, settings }) => {
      const currentLanguage = language || "en";
      const categoryLabel = settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label;
      const copy = getCardCopy(currentLanguage, "completedProjectsByCategory", {
        categoryLabel,
      });

      return {
        componentId: "completedProjectsCategoryComponent",
        labels: ["progress"],
        title: copy.title,
        description: copy.description,
      };
    }
  );
  completedProjectsCategoryComponent.mapState(
    ({ dashboard, settings, language }) => {
      const currentLanguage = language || "en";
      const categoryLabel = settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label;
      const projectsByCategoryCopy = getLocalizedCardCopy(
        currentLanguage,
        "completedProjectsByCategory",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            settings?.configuration?.projectName,
            currentLanguage
          ),
          projectNamePlural: getPluralOfProjectName(
            settings?.configuration?.projectName,
            currentLanguage
          ),
          categoryLabel,
        }
      );
      const existingCategories =
        settings?.configuration?.categoryDefinition?.map((cat) => cat.name) ??
        [];

      return {
        label:
          getLocalizedMessage(currentLanguage, "completedProjectsCategory") ||
          "Completed Projects Category",
        title: projectsByCategoryCopy.title,
        data: existingCategories.map((category) => {
          const completed =
            dashboard?._calculated?.categoryCountsForCompletedProjects?.find(
              (catCount) => catCount.label === category
            )?.value ?? 0;
          const total =
            dashboard?.projects?.filter((p) => p.category === category)
              .length ?? 0;
          return {
            label: category,
            value: completed,
            total,
            color: "#38BDF8",
          };
        }),
        showInfoIcon: false,
        showMenuIcon: false,
      };
    }
  );

  completedProjectsCategoryComponent.mapVisibility(
    ({ settings }) =>
      settings?.configuration?.fieldsDefinition
        ?.filter((field) => field.purpose === "projectBasic")
        .find((field) => field.fieldName === "category")?.enabled === true
  );

  const completedItemsPerCategoryComponent = view.addComponent({
    component: PMCategoryProgressCard,
    layoutProps: {
      componentId: "completedItemsPerCategoryComponent",
      title: "",
      description: "",
      labels: ["progress"],
    },
  });

  completedItemsPerCategoryComponent.mapLayoutProps(
    ({ language, settings }) => {
      const currentLanguage = language || "en";
      const categoryLabel = settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label;
      const copy = getCardCopy(currentLanguage, "completedItemsByCategory", {
        categoryLabel,
      });

      return {
        componentId: "completedItemsPerCategoryComponent",
        title: copy.title,
        description: copy.description,
        labels: ["progress"],
      };
    }
  );

  completedItemsPerCategoryComponent.mapState(
    ({ dashboard, settings, language }) => {
      const currentLanguage = language || "en";
      const categoryLabel = settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label;
      const itemsByCategoryCopy = getLocalizedCardCopy(
        currentLanguage,
        "completedItemsByCategory",
        {
          itemName: getItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          projectName,
          projectNamePlural,
          categoryLabel,
        }
      );
      const existingCategories =
        settings?.configuration?.categoryDefinition?.map((cat) => cat.name) ??
        [];

      return {
        label: "Completed Items Category",
        title: itemsByCategoryCopy.title,
        icon: "BarChart",
        showInfoIcon: false,
        showMenuIcon: false,
        data: existingCategories.map((category) => {
          const completed =
            dashboard?._calculated?.categoryCountsForCompletedItems?.find(
              (catCount) => catCount.label === category
            )?.value ?? 0;
          const total =
            dashboard?.projects
              ?.filter((p) => p.category === category)
              .flatMap((p) => p.reportingItems ?? []).length ?? 0;
          return {
            label: category,
            value: completed,
            total,
            color: "#38BDF8",
          };
        }),
      };
    }
  );

  completedItemsPerCategoryComponent.mapVisibility(
    ({ settings }) =>
      settings?.configuration?.fieldsDefinition
        ?.filter((field) => field.purpose === "projectBasic")
        .find((field) => field.fieldName === "category")?.enabled === true
  );
}
